import { IconChevronDown } from '@developerz/ui';
import { createUniqueId, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { RailRollup } from '../lib/rail-rollup';
import { DOT_LABEL } from './AppNavLink';

// One rail section's HEADING: the button that folds the region below it.
// Presenter — the rail (`RailGroups`) owns the fold, this paints it.
//
// A <button>, not a <summary>: it carries `aria-expanded` and `aria-controls`,
// its state is the caller's (so arriving on a page can open a section), and a
// closed section is something a unit test can assert. Click, Enter and Space
// are the element's own.
//
// While the section is FOLDED the heading speaks for the rows it hides: the
// worst status dot and the sum of what is owed (`lib/rail-rollup.ts`), in that
// order, then the chevron. Both are decoration to assistive tech: the button's
// NAME stays the heading, and the sr-only siblings wired by `aria-describedby`
// carry the words — the split `AppNavLink` draws between a badge and a name.

export function AppNavHeading(props: {
  /** This button's id: the region's `aria-labelledby`. */
  readonly id: string;
  /** Already translated. */
  readonly heading: string;
  readonly open: boolean;
  /** The id of the region this button folds. */
  readonly controls: string;
  /** What the folded rows say. `undefined` while the section is open. */
  readonly rollup: RailRollup | undefined;
  readonly onToggle: () => void;
}): JSX.Element {
  const dotId = createUniqueId();
  const countId = createUniqueId();
  const dot = (): RailRollup['dot'] => props.rollup?.dot;
  // Never 0: `rollupRail` answers `undefined` when nothing is owed.
  const count = (): number | undefined => props.rollup?.count;
  return (
    <>
      <button
        type="button"
        id={props.id}
        class="app-nav__label"
        aria-expanded={props.open}
        aria-controls={props.controls}
        aria-describedby={
          [dot() === undefined ? null : dotId, count() === undefined ? null : countId]
            .filter((v): v is string => v !== null)
            .join(' ') || undefined
        }
        onClick={(event) => {
          // Safari leaves a clicked button unfocused; fold or unfold from the heading so
          // the focus never sits on a row that is about to hide.
          event.currentTarget.focus();
          props.onToggle();
        }}
      >
        <span class="app-nav__label-text">{props.heading}</span>
        <Show when={dot()}>
          {(tone) => (
            <span
              class="app-nav__dot"
              classList={{ [`app-nav__dot--${tone()}`]: true }}
              aria-hidden="true"
            />
          )}
        </Show>
        <Show when={count()}>
          {(sum) => (
            <span class="app-nav__count" aria-hidden="true">
              {sum()}
              {props.rollup?.atLeast ? '+' : ''}
            </span>
          )}
        </Show>
        <IconChevronDown class="app-nav__chevron" size="0.875rem" />
      </button>
      <Show when={dot()}>
        {(tone) => (
          <span id={dotId} class="sr-only">
            {t('nav.rollup_dot', { status: DOT_LABEL[tone()]() })}
          </span>
        )}
      </Show>
      <Show when={count()}>
        {(sum) => (
          <span id={countId} class="sr-only">
            {t(props.rollup?.atLeast ? 'nav.rollup_count_min' : 'nav.rollup_count', {
              count: sum(),
            })}
          </span>
        )}
      </Show>
    </>
  );
}
